import { datadogRum } from '@datadog/browser-rum'
import useSetCheckoutRuleSession from '../../graphQL/mutations/setCheckoutRuleSession'
import type { ICheckoutRuleValue } from '../../graphQL/queries/getCheckoutRule/types'
import type { CheckoutRuleDocumentType } from '../../interfaces'
import { addAlert } from '../../redux/slices/config'
import { clearSelection, setSelection } from '../../redux/slices/checkout'
import { generateDataDogContext } from '../../utils/dataDog'
import getAlertErrorMessage, {
  AlertMessageType,
} from '../../utils/getAlertErrorMessage'
import useAppDispatch from '../useAppDispatch'
import useAppSelector from '../useAppSelector'
import useAppStore from '../useAppStore'
import { useDynamicBack } from '../useBack'
import usePushView from '../usePushView'

const FUNCTION_NAME = 'useCheckoutRuleSelection'

/**
 * Choosing, confirming and leaving — for either picker screen.
 *
 * The two screens' write paths differ in three values and nothing else: the
 * rule type, the toast to raise when the write fails, and the name of the
 * field their action's args arrive under. The first two are parameters here;
 * the third stays in each screen's own hook, because that is the part a
 * storefront authors and it reads better named — `setDeliveryDate({ date })`
 * rather than a generic `select`.
 *
 * Extracted rather than copied because the interesting part is the rollback,
 * and a second copy of it is a second place for a rollback bug to live. Same
 * reasoning as `useCheckoutRuleOptions` on the read side.
 */
export const useCheckoutRuleSelection = (
  ruleType: CheckoutRuleDocumentType,
  alertType: AlertMessageType,
) => {
  const dispatch = useAppDispatch()
  const store = useAppStore()
  const pushView = usePushView()
  const goBack = useDynamicBack()
  const slug = useAppSelector((state) => state.defaultSlice.storeName)
  const sessionId = useAppSelector((state) => state.defaultSlice.sessionId)
  const texts = useAppSelector((state) => state.defaultSlice.config?.texts)
  const [writeSelection] = useSetCheckoutRuleSession()

  /**
   * Records the pick optimistically, then persists it.
   *
   * **The store is read through `useAppStore`, not `useAppSelector`.** The
   * options and the previous selection are read when the shopper taps, and the
   * rollback runs a round trip after that — a render-time snapshot would be as
   * old as the closure and would restore a selection that had already moved.
   * The same reason the boot prefetch hooks read fresh (COM-974).
   *
   * `chosen` is already extracted and coerced by the caller. An empty string
   * means the args carried no usable value, and leaves the shopper where they
   * are.
   */
  const select = async (chosen: string) => {
    if (!chosen || !slug || !sessionId) return

    const checkout = store.getState().checkoutSlice
    const previous = checkout.selections[ruleType]
    /*
     * The offered option carries the `amount` the write has to echo back.
     *
     * Falling back rather than refusing on a miss. The value always comes from
     * what the screen rendered, so a miss means the option list moved under it
     * — and refusing would give the shopper a tap that does nothing. `amount`
     * is `0` on every entry both resolvers build today, so the fallback is the
     * value, not a guess.
     *
     * ⚠️ On the delivery side the fallback is also what keeps R-8 intact: the
     * token is carried through as the caller gave it, never rebuilt.
     */
    const offered = checkout.available[ruleType]?.data.find(
      (option) => option?.data === chosen,
    )
    const value: ICheckoutRuleValue = offered ?? { data: chosen, amount: 0 }

    dispatch(setSelection({ ruleType, value }))

    try {
      await writeSelection({
        variables: {
          storefrontName: slug,
          sessionUid: sessionId,
          ruleType,
          value,
        },
      })
    } catch (exception) {
      /*
       * Roll back to what was there, which is not always a selection.
       *
       * `setSelection` cannot express "there was no pick", so a failure on the
       * shopper's *first* choice needs `clearSelection` — otherwise the row
       * stays highlighted over a session that carries nothing, and the confirm
       * CTA stays enabled.
       */
      if (previous) {
        dispatch(setSelection({ ruleType, value: previous }))
      } else {
        dispatch(clearSelection({ ruleType }))
      }

      const message = getAlertErrorMessage(alertType, texts)
      if (message) dispatch(addAlert({ message }))

      const context = generateDataDogContext({
        title: 'setCheckoutRuleSession failed for a checkout rule',
        extraInfo: { function: FUNCTION_NAME, ruleType },
      })
      datadogRum.startView(context.viewName)
      datadogRum.addError(
        exception instanceof Error
          ? exception
          : new Error('setCheckoutRuleSession failed'),
        context,
      )
    }
  }

  /**
   * Confirm. Returns the shopper to the cart.
   *
   * **Deliberately not gated on a selection existing.** The CTA's own
   * `disabled` binds to `checkout.ui.<RULE>.confirmDisabled`, so a storefront
   * that authored it cannot reach this with nothing chosen — and one that did
   * not should still let the shopper leave. Landing on the cart with the rule
   * unsettled is the state COM-1046's guided flow is built to route out of; a
   * guard here would replace that with a dead button.
   *
   * COM-1046 will add `placeOrderFlowNext()` alongside the push, so the queue
   * advances to the next unchosen picker instead. One seam, one edit, for both
   * screens at once now that they share this.
   */
  const confirm = () => {
    if (!slug || !sessionId) return

    pushView({ path: `/${slug}/${sessionId}/cart` })
  }

  /**
   * Leaving without choosing.
   *
   * `useDynamicBack` rather than a bare `navigate(-1)`: this is a webview a
   * shopper can be deep-linked into, and popping an empty history strands
   * them. It falls back to the storefront home when there is nothing to pop.
   *
   * Writes nothing. Opening a picker and backing out settles nothing, which is
   * what COM-1046 reads to decide whether its CTA still says *Continuar*.
   */
  const back = () => goBack({})

  return { select, confirm, back }
}

export default useCheckoutRuleSelection
